import { useCallback, useMemo, useRef } from 'react';
import type { MapleEngine } from '../../../../packages/engine-core/src';
import { MapleGameHost } from '../../../../packages/engine-react/src';

import 'cesium/Build/Cesium/Widgets/widgets.css';
import flightSimCssRaw from './flightsim/styles/flightsim.css?raw';
import { createFlightSimGame } from './flightsim/runtime/FlightSimRuntime';

const ASSET_BASE = '/os/games/flightsim';

const scopeFlightSimCss = (css: string) =>
  css.replace(/(^|})\s*([^@{}][^{]+)\{/g, (match, closingBrace, selectorGroup) => {
    const selectors = selectorGroup
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean)
      .map((selector) => {
        if (selector.startsWith('.flight-sim-app')) return selector;
        if (/^(from|to|\d+%)$/.test(selector)) return selector;
        return `.flight-sim-app ${selector}`;
      })
      .join(',\n');

    return `${closingBrace}\n${selectors} {`;
  });

export default function FlightSim() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cesiumRef = useRef<HTMLDivElement | null>(null);
  const threeRef = useRef<HTMLDivElement | null>(null);
  const minimapCesiumRef = useRef<HTMLDivElement | null>(null);
  const pauseMinimapCesiumRef = useRef<HTMLDivElement | null>(null);
  const scopedFlightSimCss = useMemo(() => scopeFlightSimCss(flightSimCssRaw), []);

  const createGame = useCallback((engine: MapleEngine) => {
    if (!rootRef.current || !cesiumRef.current || !threeRef.current || !minimapCesiumRef.current || !pauseMinimapCesiumRef.current) {
      return;
    }

    return createFlightSimGame(engine, {
      root: rootRef.current,
      cesiumContainer: cesiumRef.current,
      threeContainer: threeRef.current,
      minimapContainer: minimapCesiumRef.current,
      pauseMinimapContainer: pauseMinimapCesiumRef.current,
    });
  }, []);

  return (
    <div ref={rootRef} className="flight-sim-app relative h-full w-full overflow-hidden bg-black text-[#0f0]">
      <style>{scopedFlightSimCss}</style>
      <div id="cesiumContainer" ref={cesiumRef} className="absolute inset-0" />
      <div id="threeContainer" ref={threeRef} className="absolute inset-0" />

      <div id="mainMenu" className="overlay">
        <div className="menu-container">
          <div className="menu-vignette" />
          <div className="menu-content">
            <div className="menu-header">
              <div className="menu-logo">
                <span className="logo-main">WEB FLIGHT</span>
                <span className="logo-sub">SIMULATOR</span>
              </div>
              <div className="menu-version">VER 1.0.0</div>
            </div>

            <div className="menu-main">
              <nav className="menu-nav">
                <button id="startBtn" className="main-menu-btn" disabled>
                  <span className="btn-top">01</span>
                  <span className="btn-mid">START GAME</span>
                  <span className="btn-bot">COMMENCE FLIGHT OPERATIONS</span>
                </button>
                <button id="optionsBtn" className="main-menu-btn">
                  <span className="btn-top">02</span>
                  <span className="btn-mid">SYSTEM CONFIG</span>
                  <span className="btn-bot">ADJUST HARDWARE SETTINGS</span>
                </button>
                <button id="helpBtn" className="main-menu-btn">
                  <span className="btn-top">03</span>
                  <span className="btn-mid">OPERATIONS MANUAL</span>
                  <span className="btn-bot">FLIGHT CONTROLS &amp; PROCEDURES</span>
                </button>
                <button id="creditsBtn" className="main-menu-btn">
                  <span className="btn-top">04</span>
                  <span className="btn-mid">CREDITS</span>
                  <span className="btn-bot">DEVELOPMENT DATA</span>
                </button>
                <button id="aboutBtn" className="main-menu-btn">
                  <span className="btn-top">05</span>
                  <span className="btn-mid">ABOUT DEVELOPER</span>
                  <span className="btn-bot">DEVELOPER INFORMATION</span>
                </button>
              </nav>
            </div>

            <div className="menu-footer">
              <div className="footer-status">
                <span className="time-label">CURRENT TIME (ZULU):</span>
                <span id="menu-time" className="time-value">2026-01-01T00:00:00Z</span>
              </div>
              <div className="footer-instruction">SELECT COMMAND TO PROCEED</div>
            </div>
          </div>
        </div>
      </div>

      <div id="loadingIndicator" className="loading-container">
        <div className="spinner" />
        <div id="loadingText" className="loading-text">Loading system...</div>
      </div>

      <div id="optionsModal" className="modal hidden">
        <div className="modal-header">
          <div className="modal-title">SYSTEM CONFIG</div>
          <button className="close-modal">CLOSE</button>
        </div>
        <div className="modal-body">
          <h3>GRAPHICS UNIT</h3>
          <div className="setting-item">
            <span>Graphics Quality</span>
            <select id="graphicsQuality" defaultValue="medium">
              <option value="low">Low (High Perf)</option>
              <option value="medium">Medium (Balanced)</option>
              <option value="high">High (High Quality)</option>
            </select>
          </div>
          <div className="setting-item">
            <span>Anti-Aliasing (FXAA)</span>
            <input type="checkbox" id="antialiasing" defaultChecked />
          </div>
          <div className="setting-item">
            <span>Fog Effects</span>
            <input type="checkbox" id="fogEffects" defaultChecked />
          </div>

          <h3>FLIGHT CONTROL</h3>
          <div className="setting-item">
            <span>Mouse Sensitivity</span>
            <div className="setting-control" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="range" id="sensitivitySlider" min="0.05" max="1.0" step="0.05" defaultValue="0.2" />
              <span id="sensitivityValue" className="setting-value">0.2</span>
            </div>
          </div>
          <div className="setting-item">
            <span>Show HUD Controls</span>
            <input type="checkbox" id="showHud" defaultChecked />
          </div>
          <div className="setting-item">
            <span>Show Horizon Lines</span>
            <input type="checkbox" id="showHorizonLines" />
          </div>
          <div className="setting-item">
            <span>Sound Active</span>
            <input type="checkbox" id="soundEnabled" defaultChecked />
          </div>
          <div className="setting-item">
            <span>Minimap Range</span>
            <select id="minimapRange" defaultValue="1">
              <option value="1">1km (Default)</option>
              <option value="5">5km (Tactical)</option>
              <option value="10">10km (Regional)</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button id="saveOptionsBtn">CONFIRM &amp; SAVE</button>
        </div>
      </div>

      <div id="helpModal" className="modal hidden">
        <div className="modal-header">
          <div className="modal-title">OPERATIONS MANUAL</div>
          <button className="close-modal">CLOSE</button>
        </div>
        <div className="modal-body">
          <h3>FLIGHT CONTROL</h3>
          <div className="control-grid">
            <div className="key">W / S</div>
            <div>Throttle Increase / Decrease</div>
            <div className="key">A / D</div>
            <div>Yaw Left / Right</div>
            <div className="key">UP / DOWN</div>
            <div>Pitch Up / Down</div>
            <div className="key">LEFT / RIGHT</div>
            <div>Roll Left / Right</div>
            <div className="key">MOUSE</div>
            <div>Steer Aircraft Pitch / Roll</div>
            <div className="key">Z</div>
            <div>Auto-Level Aircraft</div>
            <div className="key">SPACE</div>
            <div>Afterburner (Boost)</div>
          </div>
          <h3>COMBAT SYSTEMS</h3>
          <div className="control-grid">
            <div className="key">1 / 2 / Q</div>
            <div>Switch Weapons</div>
            <div className="key">F / ENTER</div>
            <div>Fire Selected Weapon</div>
            <div className="key">X</div>
            <div>Release Flares</div>
          </div>
          <h3>NAVIGATION</h3>
          <div className="control-grid">
            <div className="key">CLICK</div>
            <div>Capture Mouse / Flight Control</div>
            <div className="key">ESC / P</div>
            <div>Pause / Tactical Menu</div>
          </div>

        </div>
      </div>

      <div id="creditsModal" className="modal hidden">
        <div className="modal-header">
          <div className="modal-title">CREDITS</div>
          <button className="close-modal">CLOSE</button>
        </div>
        <div className="modal-body credits-body">
          <div className="credits-header">
            <div className="label-tiny">DEVELOPED BY</div>
            <div className="developed-by-name"><a href="https://tarmizi.id" target="_blank" rel="noreferrer">DIMAR TARMIZI</a></div>
          </div>
          <div className="credits-tech-grid">
            <div>
              <div className="label-tiny">ENGINE</div>
              <div className="tech-value">CesiumJS &amp; Three.js</div>
            </div>
            <div>
              <div className="label-tiny">ASSETS</div>
              <div className="tech-value">F-15 GLB by SIpriv</div>
            </div>
          </div>
          <p className="credits-disclaimer">THIS IS A NON-COMMERCIAL WEB-BASED FLIGHT SIMULATION PROJECT.</p>
        </div>
      </div>

      <div id="aboutBtnModal" className="modal hidden">
        <div className="modal-header">
          <div className="modal-title">DEVELOPER BIO</div>
          <button className="close-modal">CLOSE</button>
        </div>
        <div className="modal-body">
          <div className="bio-layout">
            <div className="bio-avatar-container">
              <img src={`${ASSET_BASE}/assets/images/dimar-tarmizi.jpg`} alt="Dimar Tarmizi" className="bio-avatar" />
              <div className="bio-id">ID: RYNX</div>
            </div>
            <div className="bio-details">
              <div className="bio-section">
                <div className="label-tiny">NAME</div>
                <div className="bio-name">DIMAR TARMIZI</div>
              </div>
              <div className="bio-section">
                <div className="label-tiny">SPECIFICATION</div>
                <div className="bio-spec">FULL-STACK SOFTWARE ENGINEER</div>
              </div>
              <div className="bio-description">
                Experienced in end-to-end development, deployment, and server management. Specialized in building scalable web architectures and dynamic simulation interfaces.
              </div>
              <div className="bio-social-links">
                <a href="https://tarmizi.id" target="_blank" rel="noreferrer" className="btn-portfolio">PORTFOLIO ACCESS</a>
                <a href="https://github.com/dimartarmizi" target="_blank" rel="noreferrer" className="btn-social">GITHUB</a>
                <a href="https://www.facebook.com/dimartarmizi" target="_blank" rel="noreferrer" className="btn-social">FACEBOOK</a>
                <a href="https://www.instagram.com/dimartarmizi" target="_blank" rel="noreferrer" className="btn-social">INSTAGRAM</a>
                <a href="https://www.linkedin.com/in/dimartarmizi" target="_blank" rel="noreferrer" className="btn-social">LINKEDIN</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="pauseMenu" className="overlay hidden">
        <div className="pause-content">
          <div className="pause-sidebar">
            <div className="pause-header">
              <h2 className="pause-title">PAUSED</h2>
              <div className="pause-subtitle">SYSTEM HOLD</div>
            </div>
            <nav className="pause-nav">
              <button id="resumeBtn" className="pause-btn">
                <span className="btn-label">RESUME</span>
                <span className="btn-desc">RETURN TO FLIGHT</span>
              </button>
              <button id="restartBtn" className="pause-btn">
                <span className="btn-label">RESTART</span>
                <span className="btn-desc">RESTART GAME</span>
              </button>
              <button id="pauseHelpBtn" className="pause-btn">
                <span className="btn-label">HELP</span>
                <span className="btn-desc">OPERATIONS MANUAL</span>
              </button>
              <button id="pauseOptionsBtn" className="pause-btn">
                <span className="btn-label">OPTIONS</span>
                <span className="btn-desc">SYSTEM CONFIG</span>
              </button>
              <button id="quitBtn" className="pause-btn quit">
                <span className="btn-label">QUIT</span>
                <span className="btn-desc">BACK TO MENU</span>
              </button>
            </nav>
          </div>
          <div className="pause-main">
            <div className="pause-map-wrapper">
              <div className="map-header">
                <span className="map-title">TACTICAL MAP</span>
                <span id="pause-region" className="map-location">UNKNOWN REGION</span>
              </div>
              <div id="pause-minimap-container">
                <div id="pauseMinimapCesium" ref={pauseMinimapCesiumRef} />
                <canvas id="pauseMinimap" />
              </div>
              <div className="map-footer">
                <div className="coord-item">
                  <span className="coord-label">LAT</span>
                  <span id="pause-lat" className="coord-value">0.0000</span>
                </div>
                <div className="coord-item">
                  <span className="coord-label">LON</span>
                  <span id="pause-lon" className="coord-value">0.0000</span>
                </div>
                <div className="coord-item">
                  <span className="coord-label">ALT</span>
                  <span id="pause-alt" className="coord-value">0 FT</span>
                </div>
                <div className="coord-item">
                  <span className="coord-label">LOCAL TIME</span>
                  <span id="pause-time" className="coord-value">2026-01-01T00:00:00Z</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="crashMenu" className="overlay hidden">
        <h2 className="menu-title crash-header">AIRCRAFT DESTROYED</h2>
        <button id="respawnBtn" className="menu-btn">RESPAWN</button>
      </div>

      <div id="spawnInstruction" className="hidden">
        <div id="spawn-top-ui" className="spawn-hud-top">
          <div id="spawn-instruction">
            <span id="instruction-text">CLICK ANYWHERE ON THE MAP TO CHOOSE SPAWN POINT</span>
            <input type="text" id="locationSearch" placeholder="ENTER LOCATION NAME..." />
            <div id="search-results" />
          </div>
          <div id="search-toggle-btn" title="Toggle Search">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
        </div>
        <button id="confirmSpawnBtn" className="menu-btn hidden spawn-confirm-btn">CONFIRM SPAWN POINT</button>
      </div>

      <div id="uiContainer" className="hidden">
        <div id="compass-container">
          <div id="compass-center-marker" />
          <div id="compass-tape" />
        </div>
        <div id="heading-display">000</div>

        <div id="hud-top-left">
          TIME <span id="time">00:00:00</span>
          <br />
          SCORE <span id="score">000000</span>
        </div>

        <div id="hud-top-right">
          FPS <span id="fps">60</span>
          <br />
          <span id="local-datetime">01 JAN 26 | 00:00:00</span>
        </div>

        <div id="hud-speed-box">
          <div className="label">SPEED</div>
          <div className="value-box">
            <span id="speed">0</span>
          </div>
        </div>

        <div id="hud-alt-box">
          <div className="label">ALT</div>
          <div className="value-box">
            <span id="altitude">0</span>
          </div>
        </div>

        <div id="coords">0.0000, 0.0000</div>

        <div id="region-notification" className="hidden">
          <div className="region-label">ENTERING AREA</div>
          <div id="region-name">REGION NAME</div>
        </div>

        <div id="pull-up-warning" className="hidden">PULL UP</div>

        <div id="kill-notification-container" className="hidden">
          <div id="kill-text">ENEMY DESTROYED</div>
          <div id="kill-score">+1000</div>
        </div>

        <div id="weapons-hud">
          <div id="weapon-list">
            <div className="weapon-item active" id="weapon-gun">
              <div className="weapon-progress" />
              <span className="weapon-name">M61A1 VULCAN</span>
              <span className="weapon-ammo">00</span>
            </div>
            <div className="weapon-item" id="weapon-missile">
              <div className="weapon-progress" />
              <span className="weapon-name">AIM-9 SIDEWINDER</span>
              <span className="weapon-ammo">00</span>
            </div>
            <div className="weapon-item" id="weapon-flare">
              <div className="weapon-progress" />
              <span className="weapon-name">MJU-7A</span>
              <span className="weapon-ammo">00</span>
            </div>
          </div>
          <div id="aircraft-icon">
            <img src={`${ASSET_BASE}/assets/images/f-15.svg`} alt="F-15 Icon" />
            <div id="aircraft-label">F-15</div>
          </div>
        </div>

        <div id="minimap-container">
          <div id="minimapCesium" ref={minimapCesiumRef} />
          <canvas id="minimap" />
        </div>

        <div id="game-credits">
          WEB FLIGHT SIMULATOR | CREATED BY <a href="https://tarmizi.id" target="_blank" rel="noreferrer">DIMAR TARMIZI</a> |{' '}
          <a href="https://github.com/dimartarmizi/web-flight-simulator" target="_blank" rel="noreferrer">GITHUB REPOSITORY</a>
        </div>

        <div id="dialogue-container" className="hidden">
          <div className="dialogue-box">
            <img id="commander-img" src={`${ASSET_BASE}/assets/images/commander-dimar-tarmizi.jpg`} alt="Commander" />
            <div className="dialogue-content">
              <div className="commander-name">COMMANDER DIMAR TARMIZI</div>
              <div id="dialogue-text" />
              <div className="dialogue-hint">Press Z to skip</div>
            </div>
          </div>
        </div>
      </div>

      <div id="transition-vignette" />

      <MapleGameHost
        appId="flight-sim"
        targetFPS={60}
        renderMode="webgl"
        createGame={createGame}
        className="absolute inset-0 pointer-events-none opacity-0"
      />
    </div>
  );
}